import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Button } from '@mui/material';
import { Search } from 'lucide-react';

import { useSite } from '@labradorsports/utils';

import { useNavigateWithQuery } from '../../shared/providers/index.js';
import { Selectors } from '../../store/index.js';
import { TeamsMenu, Avatar, ProgramLogo, HeaderSearchBox } from '../index.js';

interface Props {
    navExpanded: boolean;
}

const Header: FC<Props> = ({ navExpanded }) => {
    const uid = useSelector(Selectors.uid);
    const user = useSelector(Selectors.user);
    const currentProgram = useSelector(Selectors.currentProgram);

    const { Icons, Config } = useSite();
    const location = useLocation();
    const navigate = useNavigateWithQuery();
    const [showingSearch, setShowingSearch] = useState(false);

    const showSearch = (evt) => {
        evt.stopPropagation();
        setShowingSearch(true);
    };
    const hideSearch = () => setShowingSearch(false);

    const showHome = () => {
        const { pathname } = location;

        if (pathname === '/feed') {
            window.location.reload();

            return;
        }

        if (user) {
            navigate('/feed');
        } else {
            window.location.href = Config.SiteConfig.Url;
        }
    };

    const showLogin = () => {
        navigate('/login');
    };

    const classNames = ['header'];

    if (APP) {
        classNames.push('app-header');
    }

    if (navExpanded) {
        classNames.push('nav-expanded');
    }

    // APP nav has links for these pages so links here would be redundant
    const headerNavLinks: [string, string][] = [];

    if (!APP) {
        headerNavLinks.push(['Home', '/feed']);

        if (Config.Features.PlayDesigner) {
            headerNavLinks.push(['Playbook', '/playbook']);
        }
        if (Config.Features.Roster && Config.Features.TeamAdmin) {
            headerNavLinks.push(['Roster & Depth Chart', '/team/roster']);
        }
        if (Config.Features.Schedule && Config.Features.TeamAdmin) {
            headerNavLinks.push(['Schedule', '/schedule']);
        }
    }

    if (!user) {
        return (
            <div className="logged-out-header">
                <div className="site-logo-container">
                    <Icons.FullLogo className="site-logo" fill="black" onClick={showLogin} />
                </div>
                <Button component="a" href="/login" variant="outlined" color="primary" className="d-none d-sm-block">
                    Login
                </Button>
                <Button component="a" href="/signup" variant="outlined" color="secondary">
                    Signup
                </Button>
            </div>
        );
    }

    return (
        <div className={classNames.join(' ')}>
            <span onClick={showHome} className="logo">
                <ProgramLogo program={currentProgram} fallbackToSiteLogo key={uid} />
            </span>
            <div className="header-buttons">
                {user && Config.Features.TeamAdmin ? <TeamsMenu /> : null}
                <div className="d-lg-none">
                    {user ? <HeaderSearchBox shown={showingSearch} onHide={hideSearch} /> : null}
                    {user ? (
                        <button className="search-button" onClick={showSearch}>
                            <Search className="button-icon search-icon" />
                        </button>
                    ) : null}
                </div>
            </div>
            {user ? (
                <div className="d-lg-none" onClick={() => navigate('/profile/account/basic')}>
                    <Avatar />
                </div>
            ) : null}
        </div>
    );
};

export default Header;
